import { useState, useEffect, useRef } from "react";
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import numberUtilities from "../../utilities/numberUtilities";
import Api from "../api/api";
import { ReportsCharts } from "./reportsCharts";
import { dateRange, toDateString } from "../../utilities/dateUtilities";
import { reportDatePresetByLabel } from "./reportsEnums";
import { saveAs } from 'file-saver';
import { EmptyState } from "../dashboard/emptyState";
import Loader from "../shared/loader";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { AppliedFilterDisplay } from "../shared/appliedFilterDisplay";
import { DataStorageReportFiltersModal } from "./filters-modals/dataStorageReportFiltersModal";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";
import { DataStorageScheduleModal } from "./schedule-modals/dataStorageScheduleModal";

export const DataStorageReport = ({ organisationId, organisationName, organisationLogo, token }) => {

    const [reportsData, setReportsData] = useState(null);
    const [fromDate, setFromDate] = useState(dateRange("lastMonth").firstDate);
    const [toDate, setToDate] = useState(dateRange("lastMonth").lastDate);
    const [datePreset, setDatePreset] = useState(reportDatePresetByLabel["Last month"]);
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const reportsLandingUrl = `/organisation/${ organisationId }/reports`;
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const pdfRef = useRef()

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setReportsData(null);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
        });
        setShowFilterPills(true);
        fetchDataStorageReport(defaultRange.firstDate, defaultRange.lastDate);
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Date Range": {
                const defaultRange = dateRange("lastMonth")
                setFromDate(defaultRange.firstDate)
                setToDate(defaultRange.lastDate)
                setDatePreset(reportDatePresetByLabel["Last month"])
                fetchDataStorageReport(defaultRange.firstDate, defaultRange.lastDate)
                break
            }
            default:
                break
        }
    }

    const fetchDataStorageReport = (from, to) => {
        setIsLoading(true)
        Api.getDataStorageReport(
            data => {
                setReportsData(data)
                setIsLoading(false)
            },
            error => {
                console.log(error);
                setIsLoading(false)
            },
            from,
            to,
            0,
            token,
            organisationId
        )
    }

    const handleDownload = () => {
        Api.getDataStorageReport(
            (blob, filename) => { saveAs(blob, filename) },
            error => {
                console.log(error);
                setIsLoading(false)
            },
            fromDate,
            toDate,
            1,
            token,
            organisationId
        )
    }


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchDataStorageReport(fromDate, toDate);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`
        });
        setShowFilterPills(true);
    }, [])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(reportsData)) {
            setChartData(
                reportsData.map(
                    item => (
                        {
                            label: toDateString(item.date),
                            "Database": numberUtilities.convertMegabytesToGigabytes(item.databaseSizeMb),
                            "Attachments": numberUtilities.convertMegabytesToGigabytes(item.attachmentsSizeMb),
                            "Camera Images": numberUtilities.convertMegabytesToGigabytes(item.cameraImagesSizeMb),
                        }
                    )
                )
            )
        }
    }, [reportsData])

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h5 className="mb-0">
                                            Data Storage
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex mr-2 align-items-center">
                                            <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                        </div>
                                        <button
                                            onClick={ () => handleDownload() }
                                            className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                        >
                                            Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - Data Storage`) }
                                            className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                        >
                                            Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                        </button>
                                        <button
                                            onClick={ () => setShowScheduleExportModal(true) }
                                            className="btn btn-sm btn-secondary nowrap mr-2"
                                        >
                                            Schedule Report<i className="ml-2 far fa-clock"></i>
                                        </button>
                                        <Link
                                            className="btn btn-falcon-default btn-sm mr-3"
                                            type="button"
                                            to={ reportsLandingUrl }>
                                            <i className="fas fa-reply"></i> Back
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body border-top d-flex flex-column">
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label, value]) => {
                                                    const isResettable = label === "Date Range"
                                                    return !stringHelpers.isNullOrEmpty(value)
                                                    && <div className="mr-2" key={ label }>
                                                        <AppliedFilterDisplay
                                                            label={ label }
                                                            isResettable={ isResettable }
                                                            onTextClick={ () => setShowFiltersModal(true) }
                                                            onCloseClick={ closedLabel => {
                                                                clearFilterByLabel(closedLabel)
                                                                setAppliedFilters(prevState => {
                                                                    const updatedFilters = { ...prevState }
                                                                    if (closedLabel === "Date Range") {
                                                                        const defaultRange = dateRange("lastMonth")
                                                                        updatedFilters["Date Range"] = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
                                                                    } else {
                                                                        updatedFilters[closedLabel] = null
                                                                    }
                                                                    const hasRemainingFilters = Object.values(updatedFilters).some(value => !stringHelpers.isNullOrEmpty(value))
                                                                    return hasRemainingFilters ? updatedFilters : null
                                                                })
                                                            } }
                                                            value={ value }
                                                        />
                                                    </div>
                                                }
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }
                                    </div>
                                }
                                <div ref={ pdfRef }>
                                    <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="Data Storage">
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Range</span>
                                            <span className="print-only-filter-value">
                                                { toDateString(fromDate) } - { toDateString(toDate) }
                                            </span>
                                        </div>
                                    </PrintPdfHeader>
                                {
                                    isLoading
                                        ? <Loader />
                                        : <>
                                            {
                                                !arrayUtilities.isNullOrEmpty(reportsData)
                                                    ? (
                                                        <div className="mt-3 d-flex flex-column">
                                                            {
                                                                chartData &&
                                                                <ReportsCharts tooltipValueSuffix=" GB" stackId="stack" showLegend data={ chartData } chartStyle="bar" isGroupedChart tooltipValueDecimalPrecision={ 2 } xAxisInterval={ Math.max(0, Math.ceil(chartData.length / 30) - 1) } />
                                                            }
                                                            <div className="card-body bg-white border-top p-0">
                                                                <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                                    <thead className="bg-200 text-900">
                                                                        <tr>
                                                                            <th>Date</th>
                                                                            <th className="text-right">Database (GB)</th>
                                                                            <th className="text-right">Attachments (GB)</th>
                                                                            <th className="text-right">Camera Images (GB)</th>
                                                                            <th className="text-right">Total Storage (GB)</th>
                                                                            <th className="text-right">Delta (GB)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            !arrayUtilities.isNullOrEmpty(reportsData) && reportsData.map((item, index) =>
                                                                                <tr key={ index }>
                                                                                    <td>{ toDateString(item.date) }</td>
                                                                                    <td className="text-right">
                                                                                        { stringHelpers.formatDecimal(numberUtilities.convertMegabytesToGigabytes(item.databaseSizeMb), 2) }
                                                                                    </td>
                                                                                    <td className="text-right">
                                                                                        { stringHelpers.formatDecimal(numberUtilities.convertMegabytesToGigabytes(item.attachmentsSizeMb), 2) }
                                                                                    </td>
                                                                                    <td className="text-right">
                                                                                        { stringHelpers.formatDecimal(numberUtilities.convertMegabytesToGigabytes(item.cameraImagesSizeMb), 2) }
                                                                                    </td>
                                                                                    <td className="text-right">
                                                                                        { stringHelpers.formatDecimal(numberUtilities.convertMegabytesToGigabytes(item.totalStorageMb), 2) }
                                                                                    </td>
                                                                                    <td className={ `text-right ${ item.deltaMb > 0 ? "text-danger" : "text-success" }` }>
                                                                                        <span>{ item.deltaMb > 0 ? <>+</> : <>-</> }{ stringHelpers.formatDecimal(numberUtilities.convertMegabytesToGigabytes(Math.abs(item.deltaMb)), 2) }</span>
                                                                                    </td>
                                                                                </tr>)
                                                                        }
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                    ) : <EmptyState message="No Data available for the selected filters" />
                                            }
                                        </>
                                }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <DataStorageReportFiltersModal
                    showModal={ showFiltersModal }
                    handleClose={ () => setShowFiltersModal(false) }
                    handleConfirm={ (draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        fetchDataStorageReport(draftFilters.fromDate, draftFilters.toDate)
                    } }
                    initialFromDate={ fromDate }
                    initialToDate={ toDate }
                    initialDatePreset={ datePreset }
                    initialAppliedFilters={ appliedFilters }
                    key="data-storage-report-filters-modal"
                />
            }
            {
            showScheduleExportModal &&
            <DataStorageScheduleModal
                organisationId={ organisationId }
                token={ token }
                initialParameters={ scheduleInitialParameters }
                onCancel={ closeScheduleExportModal }
                onSaved={ closeScheduleExportModal }
            />
        }
        </>
    )
}
